import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { wahooIntegrationService } from '../services/wahooIntegrationService';

const WahooCallbackPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const code = searchParams.get('code');
        const error = searchParams.get('error');

        if (error) {
            setStatus('error');
            setErrorMessage(error);
            return;
        }

        if (code) {
            wahooIntegrationService.handleCallback(code)
                .then(() => {
                    setStatus('success');
                    // Redirect back to profile (or home for mobile) after short delay
                    const isMobile = window.innerWidth < 768;
                    setTimeout(() => navigate(isMobile ? '/' : '/profile'), 2000);
                })
                .catch((err) => {
                    console.error(err);
                    setStatus('error');
                    setErrorMessage(err.message || 'Failed to connect Wahoo account.');
                });
        } else {
            setStatus('error');
            setErrorMessage('No authorization code received.');
        }
    }, [searchParams, navigate]);

    return (
        <div className="flex flex-col items-center justify-center h-screen bg-gray-900 text-white">
            <div className="bg-gray-800 p-8 rounded-lg shadow-xl border border-gray-700 max-w-md w-full text-center">
                {status === 'loading' && (
                    <>
                        <svg className="animate-spin h-10 w-10 text-cyan-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <h2 className="text-xl font-bold">Connecting to Wahoo...</h2>
                        <p className="text-gray-400 mt-2">Please wait while we verify your account.</p>
                    </>
                )}
                {status === 'success' && (
                    <>
                        <div className="text-green-500 text-5xl mb-4">✓</div>
                        <h2 className="text-xl font-bold">Connected!</h2>
                        <p className="text-gray-400 mt-2">Redirecting you back to your profile...</p>
                    </>
                )}
                {status === 'error' && (
                    <>
                        <div className="text-red-500 text-5xl mb-4">✗</div>
                        <h2 className="text-xl font-bold">Connection Failed</h2>
                        <p className="text-red-300 mt-2">{errorMessage}</p>
                        <button 
                            onClick={() => navigate(window.innerWidth < 768 ? '/' : '/profile')}
                            className="mt-6 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded text-white font-bold transition-colors"
                        >
                            Return to Profile
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default WahooCallbackPage;